import { Warp } from "@paper-design/shaders-react";
import { memo } from "react";

import { cn } from "../cn.ts";
import { ClientOnly } from "../layout/client-only.tsx";
import { useLiveShader } from "./ambient.ts";
import { isNightLook, useMarbleLook, useMarbleTuning } from "./marble-looks.ts";
import type { MarbleFlow } from "./marble-looks.ts";

// Poured paint behind a marketing hero: slow marbled veins of cream, rose and
// mauve with the odd chartreuse patch. A painted gradient is there from the
// first frame; the shader fades in over it. A veil in the page's own ground
// sits between the paint and the words, and the paint fades into the page at
// the bottom so the next band starts clean.

/** What shows before the shader runs, or where it cannot: the same paint, still. */
const PAINTED = {
  day: "bg-[radial-gradient(60%_50%_at_15%_20%,var(--marble-lilac),transparent_70%),radial-gradient(55%_50%_at_85%_60%,var(--marble-mauve),transparent_70%),radial-gradient(70%_60%_at_50%_40%,var(--marble-cream),transparent_80%),linear-gradient(var(--marble-rose),var(--marble-rose))]",
  night:
    "bg-[radial-gradient(60%_55%_at_80%_20%,var(--tide-4),transparent_70%),radial-gradient(70%_60%_at_25%_55%,var(--tide-3),transparent_75%),radial-gradient(60%_60%_at_60%_85%,var(--tide-2),transparent_80%),linear-gradient(var(--tide-1),var(--tide-1))]",
} as const;

/** Where the veil clears the paint for the words: the middle, or the left where a flush-left headline sits. */
const VEIL = {
  center: "bg-[radial-gradient(42%_46%_at_50%_42%,var(--marble-veil)_40%,transparent_100%)]",
  start: "bg-[radial-gradient(62%_64%_at_26%_46%,var(--marble-veil)_52%,transparent_100%)]",
} as const;

/**
 * The landing hero's box: 937px tall on every desktop width (its content sets
 * it). A page that shows the landing's paint without the landing's content
 * (signing in, the splash) paints this box from the top, so the Warp scales
 * the same and the fade lands in the same place; below it is the page.
 */
export function LandingPaint({ focus = "start" }: { readonly focus?: keyof typeof VEIL }) {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-[58.5625rem]">
      <MarbleBackdrop focus={focus} />
    </div>
  );
}

export const MarbleBackdrop = memo(function MarbleBackdrop({
  flow = "veins",
  focus = "center",
}: {
  readonly flow?: MarbleFlow;
  readonly focus?: keyof typeof VEIL;
}) {
  const look = useMarbleLook(flow);
  const shader = useLiveShader(look.tokens);
  // An open tuner wins over the preset and the tokens, so its sliders show live.
  const tuning = useMarbleTuning();
  const { speed, ...preset } = tuning?.settings ?? look.settings;
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
      <div className={cn("absolute inset-0", isNightLook(look.name) ? PAINTED.night : PAINTED.day)} />
      {shader ? (
        // Belt and suspenders: `shader` is already undefined on the server and on the first
        // hydration pass (ADR-027 point 4), so this never actually gates anything today, but
        // it says, in code, that a WebGL layer never renders outside the client.
        <ClientOnly>
          <Warp
            className="absolute inset-0 size-full animate-fade-in"
            colors={tuning?.colors ? [...tuning.colors] : shader.colors}
            {...preset}
            speed={shader.still ? 0 : speed}
            minPixelRatio={1}
            maxPixelCount={1920 * 1080}
          />
        </ClientOnly>
      ) : null}
      {/* What sits over the paint so the page reads on it. An open tuner can switch all of it off, to see the Warp as it is. */}
      {tuning?.overlays === false ? null : (
        <>
          <div className="absolute inset-0 bg-(--marble-dim)" />
          <div className={cn("absolute inset-0", VEIL[focus])} />
          {/* The header's band: its links sit on the page's own ground, whatever the paint does. */}
          <div className="absolute inset-x-0 top-0 h-28 bg-linear-to-b from-bg/90 via-bg/60 to-transparent" />
          {/* The paint fades into the page on a smoothstep curve (eased at both ends), so no edge shows where the next band starts. */}
          <div className="absolute inset-x-0 bottom-0 h-1/2 bg-[linear-gradient(to_bottom,transparent,color-mix(in_oklch,var(--bg)_3%,transparent)_10%,color-mix(in_oklch,var(--bg)_10%,transparent)_20%,color-mix(in_oklch,var(--bg)_22%,transparent)_30%,color-mix(in_oklch,var(--bg)_35%,transparent)_40%,color-mix(in_oklch,var(--bg)_50%,transparent)_50%,color-mix(in_oklch,var(--bg)_65%,transparent)_60%,color-mix(in_oklch,var(--bg)_78%,transparent)_70%,color-mix(in_oklch,var(--bg)_90%,transparent)_80%,color-mix(in_oklch,var(--bg)_97%,transparent)_90%,var(--bg))]" />
        </>
      )}
    </div>
  );
});
